/* DAILE v2 (dv) — Gift sets (sections/dv-sets.liquid).
 *
 * One set-detail <dialog> per section. Card buttons carry
 * data-dv-sets-open="<block id>"; the data for every set is rendered by Liquid
 * into <script type="application/json" data-dv-sets-data> (prices already
 * formatted with the market's money format, links already localised).
 * Opening goes through DV.openDialog: modal, Escape, backdrop click, body
 * scroll lock and focus return to the card that opened it.
 *
 * Photo column = slideshow of set.images (card photo first, then the price
 * product's "#set:" photos in admin order). Round 5 (owner): it plays on its
 * own from the moment the window opens, every 2.5 s; paused while the mouse is
 * over the photo, while keyboard focus is on its arrows, while a finger is on
 * it and while the tab is hidden. As soon as the visitor changes the photo
 * (arrow, swipe, ←/→ keys) the autoplay stops for the rest of that opening;
 * the next opening plays again. There is no play/pause button. Arrows sit at
 * the sides of the photo, the "3 / 9" counter is a small tag below.
 * prefers-reduced-motion: never plays on its own (arrows / swipe only).
 * Only the current and the neighbouring slides are downloaded.
 * Each set may take its photos from its own product (block "photos_product",
 * resolved in Liquid). Slides flagged "glue" (alt mentions glue) carry a small
 * paper caption (data-glue-label on the track) in the bottom-start corner.
 * Phone (≤ 700px): every set opens as the same window — the copy column is
 * as tall as the longest set's copy (measured, --dv-sets-copy-min).
 */
(() => {
  'use strict';

  const AUTOPLAY_MS = 2500;
  const SWIPE_MIN = 40;        // px of horizontal travel that counts as a swipe
  const LOAD_WAIT_MS = 1200;   // max wait for a slide's image before showing it anyway
  const FADE_MS = 900;         // a bit longer than the CSS crossfade (.7s)

  const createSlideshow = (DV, dialog) => {
    const box = dialog.querySelector('[data-dv-sets-gallery]');
    const track = box && box.querySelector('[data-dv-sets-slides]');
    if (!box || !track) return null;

    const nav = box.querySelector('[data-dv-sets-nav]');
    const prev = box.querySelector('[data-dv-sets-prev]');
    const next = box.querySelector('[data-dv-sets-next]');
    const current = box.querySelector('[data-dv-sets-current]');
    const total = box.querySelector('[data-dv-sets-total]');
    const sizes = track.getAttribute('data-sizes') || '100vw';
    // "Glue Kit" caption for slides whose alt mentions glue (item.glue, set by Liquid).
    const glueLabel = (track.getAttribute('data-glue-label') || '').trim();

    let slides = [];      // { el, img, item, requested }
    let index = 0;
    let wanted = false;   // autoplay on for this opening (off for reduced motion / after the visitor navigates)
    let hovering = false;
    let focused = false;
    let touching = false;
    let timer = 0;
    let leaveTimer = 0;
    let token = 0;        // guards async slide changes against newer ones
    let swipe = null;

    const reduced = () => !!(DV.reducedMotion && DV.reducedMotion.matches);
    const rtl = () => (typeof DV.isRTL === 'function' ? DV.isRTL() : document.documentElement.dir === 'rtl');

    const request = (slide) => {
      if (!slide || slide.requested) return;
      slide.requested = true;
      const { img, item } = slide;
      // srcset before src, so the browser never fetches a wrong candidate.
      if (item.srcset) img.setAttribute('srcset', item.srcset);
      img.setAttribute('src', item.src);
    };

    const whenReady = (img) => new Promise((resolve) => {
      if (img.complete && img.naturalWidth) {
        resolve();
        return;
      }
      const done = () => {
        clearTimeout(limit);
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve();
      };
      const limit = setTimeout(done, LOAD_WAIT_MS);
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });

    const running = () => dialog.open && slides.length > 1 && wanted
      && !hovering && !focused && !touching && !document.hidden;

    const schedule = () => {
      clearTimeout(timer);
      timer = 0;
      if (running()) {
        timer = setTimeout(() => {
          timer = 0;
          go(index + 1);
        }, AUTOPLAY_MS);
      }
    };

    // Screen readers: silent while rotating, announce manual changes.
    const syncLive = () => {
      track.setAttribute('aria-live', wanted ? 'off' : 'polite');
    };

    const preloadAround = () => {
      const n = slides.length;
      if (n < 2) return;
      request(slides[(index + 1) % n]);
      request(slides[(index - 1 + n) % n]);
    };

    const show = (target) => {
      const from = slides[index];
      const to = slides[target];
      if (!to || to === from) return;
      clearTimeout(leaveTimer);
      slides.forEach((slide) => slide.el.classList.remove('is-leaving'));
      from.el.classList.remove('is-active');
      from.el.classList.add('is-leaving');
      from.el.setAttribute('aria-hidden', 'true');
      to.el.classList.add('is-active');
      to.el.removeAttribute('aria-hidden');
      index = target;
      if (current) current.textContent = String(index + 1);
      leaveTimer = setTimeout(() => from.el.classList.remove('is-leaving'), reduced() ? 0 : FADE_MS);
      preloadAround();
      schedule();
    };

    const go = (to) => {
      const n = slides.length;
      if (n < 2) return;
      const target = ((to % n) + n) % n;
      if (target === index) return;
      const slide = slides[target];
      const mine = ++token;
      request(slide);
      clearTimeout(timer);
      timer = 0;
      whenReady(slide.img).then(() => {
        if (mine === token && dialog.open) show(target);
      });
    };

    const makeSlide = (item, i, n) => {
      const el = document.createElement('div');
      el.className = 'dv-sets-dialog__slide';
      el.setAttribute('role', 'group');
      el.setAttribute('aria-roledescription', 'slide');
      el.setAttribute('aria-label', `${i + 1} / ${n}`);
      const img = document.createElement('img');
      img.setAttribute('sizes', sizes);
      if (item.width) img.setAttribute('width', item.width);
      if (item.height) img.setAttribute('height', item.height);
      img.setAttribute('alt', item.alt || '');
      img.setAttribute('decoding', 'async');
      img.setAttribute('draggable', 'false');
      el.append(img);
      if (item.glue && glueLabel) {
        // Decorative: the photo's alt already names the glue kit.
        const caption = document.createElement('span');
        caption.className = 'dv-sets-dialog__caption';
        caption.setAttribute('aria-hidden', 'true');
        caption.textContent = glueLabel;
        el.append(caption);
      }
      return { el, img, item, requested: false };
    };

    /* Build the slides of one set. Only the first image is requested now;
       the neighbours follow once it is on screen. */
    const load = (images) => {
      stop();
      token += 1;
      const list = (Array.isArray(images) ? images : []).filter((item) => item && item.src);
      slides = list.map((item, i) => makeSlide(item, i, list.length));
      index = 0;
      hovering = false;
      focused = false;
      touching = false;
      swipe = null;
      track.replaceChildren(...slides.map((slide) => slide.el));
      slides.forEach((slide, i) => {
        if (i === 0) slide.el.classList.add('is-active');
        else slide.el.setAttribute('aria-hidden', 'true');
      });
      if (slides[0]) {
        slides[0].img.setAttribute('fetchpriority', 'high');
        request(slides[0]);
      }
      if (current) current.textContent = '1';
      if (total) total.textContent = String(slides.length);
      if (nav) nav.hidden = slides.length < 2;
      wanted = !reduced();
      syncLive();
    };

    // Called right after the dialog is shown.
    const start = () => {
      if (slides[0]) whenReady(slides[0].img).then(() => { if (dialog.open) preloadAround(); });
      schedule();
    };

    function stop() {
      clearTimeout(timer);
      clearTimeout(leaveTimer);
      timer = 0;
      token += 1;
    }

    /* Controls. Any change made by the visitor ends the autoplay of this opening. */
    const step = (delta) => {
      if (wanted) {
        wanted = false;
        syncLive();
      }
      go(index + delta);
    };
    const onPrev = () => step(-1);
    const onNext = () => step(1);

    const onKeydown = (event) => {
      if (slides.length < 2 || event.altKey || event.ctrlKey || event.metaKey) return;
      const back = rtl() ? 'ArrowRight' : 'ArrowLeft';
      const forward = rtl() ? 'ArrowLeft' : 'ArrowRight';
      if (event.key === back || event.key === forward) {
        event.preventDefault();
        step(event.key === forward ? 1 : -1);
      }
    };

    /* Pause while hovered (mouse only), while keyboard focus is inside (only
       focus-visible: a mouse click on an arrow must not freeze the show), and
       while a finger or pen is down. */
    const onEnter = (event) => {
      if (event.pointerType !== 'mouse') return;
      hovering = true;
      schedule();
    };
    const onLeave = (event) => {
      if (event.pointerType !== 'mouse') return;
      hovering = false;
      schedule();
    };
    const onFocusIn = (event) => {
      let visible = false;
      try {
        visible = event.target.matches(':focus-visible');
      } catch (error) {
        visible = true;
      }
      focused = visible;
      schedule();
    };
    const onFocusOut = (event) => {
      if (event.relatedTarget && box.contains(event.relatedTarget)) return;
      focused = false;
      schedule();
    };

    const onDown = (event) => {
      if (event.pointerType !== 'mouse') {
        touching = true;
        schedule();
      }
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest && event.target.closest('button')) return;
      swipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
    };
    const onUp = (event) => {
      const started = swipe;
      swipe = null;
      if (started && started.id === event.pointerId) {
        const dx = event.clientX - started.x;
        const dy = event.clientY - started.y;
        if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy) * 1.2) {
          // LTR: finger to the left = next photo; RTL mirrors it.
          const forward = rtl() ? dx > 0 : dx < 0;
          step(forward ? 1 : -1);
        }
      }
      if (event.pointerType !== 'mouse' && touching) {
        touching = false;
        schedule();
      }
    };
    const onCancel = (event) => {
      swipe = null;
      if (event.pointerType !== 'mouse' && touching) {
        touching = false;
        schedule();
      }
    };

    const onVisibility = () => schedule();
    const onMotion = () => {
      wanted = !reduced() && wanted;
      syncLive();
      schedule();
    };
    const onClose = () => stop();

    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);
    box.addEventListener('keydown', onKeydown);
    box.addEventListener('pointerenter', onEnter);
    box.addEventListener('pointerleave', onLeave);
    box.addEventListener('focusin', onFocusIn);
    box.addEventListener('focusout', onFocusOut);
    box.addEventListener('pointerdown', onDown);
    box.addEventListener('pointerup', onUp);
    box.addEventListener('pointercancel', onCancel);
    document.addEventListener('visibilitychange', onVisibility);
    dialog.addEventListener('close', onClose);
    const offMotion = typeof DV.onMediaChange === 'function' && DV.reducedMotion
      ? DV.onMediaChange(DV.reducedMotion, onMotion)
      : () => {};

    return {
      load,
      start,
      stop,
      destroy() {
        stop();
        if (prev) prev.removeEventListener('click', onPrev);
        if (next) next.removeEventListener('click', onNext);
        box.removeEventListener('keydown', onKeydown);
        box.removeEventListener('pointerenter', onEnter);
        box.removeEventListener('pointerleave', onLeave);
        box.removeEventListener('focusin', onFocusIn);
        box.removeEventListener('focusout', onFocusOut);
        box.removeEventListener('pointerdown', onDown);
        box.removeEventListener('pointerup', onUp);
        box.removeEventListener('pointercancel', onCancel);
        document.removeEventListener('visibilitychange', onVisibility);
        dialog.removeEventListener('close', onClose);
        offMotion();
      },
    };
  };

  const setup = () => {
    if (!window.DV || typeof window.DV.register !== 'function') return false;

    window.DV.register('dv-sets', (root) => {
      const DV = window.DV;
      const section = DV.queryAll(root, '[data-dv-section="sets"]')[0];
      if (!section) return undefined;

      const dialog = section.querySelector('dialog[data-dv-sets-dialog]');
      const store = section.querySelector('script[data-dv-sets-data]');
      if (!dialog || !store) return undefined;

      let data = {};
      try {
        data = JSON.parse(store.textContent) || {};
      } catch (error) {
        data = {};
      }

      const slideshow = createSlideshow(DV, dialog);
      const fieldIn = (scope, name) => scope.querySelector(`[data-dv-sets-field="${name}"]`);

      // Money strings come from the shop's money format and may carry entities (&euro;).
      const decoder = document.createElement('textarea');
      const decode = (value) => {
        if (!value || value.indexOf('&') === -1) return value || '';
        decoder.innerHTML = value;
        return decoder.value;
      };

      const setText = (scope, name, value, keepVisible) => {
        const el = fieldIn(scope, name);
        if (!el) return;
        el.textContent = value || '';
        el.hidden = !keepVisible && !value;
      };

      const setLink = (scope, name, href) => {
        const el = fieldIn(scope, name);
        if (!el) return;
        if (href) {
          el.setAttribute('href', href);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      };

      // Text column of one set (the dialog itself, or the hidden measuring copy).
      const fillCopy = (scope, set) => {
        setText(scope, 'name', set.name, true);
        setText(scope, 'price', decode(set.price));
        setText(scope, 'spec', set.spec);
        setText(scope, 'description', set.description);
        setText(scope, 'addon', decode(set.addon));

        const list = fieldIn(scope, 'includes');
        if (list) {
          const items = Array.isArray(set.includes) ? set.includes : [];
          list.replaceChildren(...items.map((text) => {
            const li = document.createElement('li');
            li.textContent = text;
            return li;
          }));
          list.hidden = !items.length;
        }

        setLink(scope, 'personalise', set.personalise);
        setLink(scope, 'art', set.art);
      };

      const fill = (set) => {
        fillCopy(dialog, set);

        if (slideshow) {
          const images = Array.isArray(set.images) ? set.images : (set.image ? [set.image] : []);
          if (images.length) slideshow.load(images);
        }

        dialog.setAttribute('data-dv-sets-key', set.key || '');
        dialog.scrollTop = 0;
      };

      /* Phone: every set opens as the same window. The copy column gets the
         height of the longest set's copy at the current width (a hidden copy
         of the column is filled with each set in turn and measured), so the
         photo well and the buttons sit at the same place for all four sets.
         dv-sets.css uses --dv-sets-copy-min only below 700px. */
      const phone = window.matchMedia('(max-width: 700px)');
      let measuredWidth = 0;
      const equalise = (force) => {
        if (!phone.matches || !dialog.open) return;
        const copy = dialog.querySelector('.dv-sets-dialog__copy');
        const width = copy ? copy.getBoundingClientRect().width : 0;
        if (!width || (!force && width === measuredWidth)) return;
        const probe = copy.cloneNode(true);
        probe.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        probe.setAttribute('aria-hidden', 'true');
        probe.inert = true;
        Object.assign(probe.style, {
          position: 'absolute',
          insetBlockStart: '0',
          insetInlineStart: '0',
          width: `${width}px`,
          height: 'auto',
          minHeight: '0',
          maxHeight: 'none',
          visibility: 'hidden',
          pointerEvents: 'none',
        });
        dialog.append(probe);
        let tallest = 0;
        Object.keys(data).forEach((id) => {
          fillCopy(probe, data[id]);
          tallest = Math.max(tallest, probe.getBoundingClientRect().height);
        });
        probe.remove();
        if (tallest) {
          measuredWidth = width;
          dialog.style.setProperty('--dv-sets-copy-min', `${Math.ceil(tallest)}px`);
        }
      };
      const onResize = () => {
        if (dialog.open) equalise(false);
      };
      window.addEventListener('resize', onResize);
      if (document.fonts && document.fonts.ready) {
        // Web fonts change line breaks: measure again once they are in.
        document.fonts.ready.then(() => {
          measuredWidth = 0;
          if (dialog.open) equalise(true);
        });
      }

      const openers = DV.queryAll(section, '[data-dv-sets-open]');

      const onOpen = (event) => {
        const trigger = event.currentTarget;
        const set = data[trigger.getAttribute('data-dv-sets-open')];
        if (!set) return;
        event.preventDefault();
        fill(set);
        DV.openDialog(dialog, trigger);
        equalise(false);
        if (slideshow) slideshow.start();
      };

      openers.forEach((button) => button.addEventListener('click', onOpen));

      const closeIfOpen = () => {
        if (dialog.open) dialog.close();
      };

      return {
        destroy() {
          openers.forEach((button) => button.removeEventListener('click', onOpen));
          window.removeEventListener('resize', onResize);
          closeIfOpen();
          if (slideshow) slideshow.destroy();
        },
        // Theme editor: never leave a modal over the preview while editing.
        onBlockSelect: closeIfOpen,
        onSectionDeselect: closeIfOpen,
      };
    });

    return true;
  };

  if (!setup()) document.addEventListener('DOMContentLoaded', setup, { once: true });
})();
